import { NewPassword } from '../../components/NewPassword';
import { useBackForward } from '../../useBackForward';
import { errorText } from '../../errorText';
import { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { joinOrganizationSchema } from './schema';
import {Flex, Button, Text, Callout} from '@dodilio/ui-design-lib';
import {JoinOrganizationRequest} from '../../types/register';
import {z} from "zod";
import {useParams} from 'react-router-dom';
import {ServiceRegister} from "../../api";
import { auth, ownSession, signIn } from '../../auth';
import { readApiError, signInAsFrom } from '../../apiError';
import { leaveWithSession } from '../../handoff';
import { keycloakLink } from '../../signInLink';
import { followSignInLink } from '../../followSignInLink';

type FormData = z.infer<typeof joinOrganizationSchema>;

/**
 * The logged-out path.
 *
 * Which door is correct depends on whether the invited address already has an
 * account, and the page is deliberately not told that: `GetInvite` classifies
 * the CALLER's session and never the invited address, because an invite code
 * arrives by email and answering would make it an email-enumeration oracle.
 *
 * So the page does not guess and does not offer two doors — it ASKS. "Accept
 * invitation" attempts the join with no password, and the server's refusal
 * names the path:
 *
 *   401 (SignInRequired)   -> the address has an account. Go to sign-in; there
 *                             is never a password field.
 *   400 (InvalidPassword)  -> no account yet. NOW ask for a password.
 *
 * Offering both at once was the bug: an existing customer was shown a
 * create-a-password form for an account they already had.
 */
export const AcceptInviteForm = ({ organizationName }: { organizationName?: string }) => {
    const { code } = useParams<{ code: string }>();
    const [status, setStatus] = useState<'idle' | 'info' | 'success' | 'error'>('idle');
    const [message, setMessage] = useState<string>('');
    const [hasAccepted, setHasAccepted] = useState(false);
    const [isJoining, setIsJoining] = useState(false);
    // The accept click is a round trip, so the button has to show it is working
    // or a slow answer reads as a dead control.
    const [isChecking, setIsChecking] = useState(false);
    // Back from Keycloak or the console: the buttons work again.
    useBackForward(useCallback(() => { setIsChecking(false); setIsJoining(false); setStatus('idle'); }, []));
    const defaultRedirect = import.meta.env.VITE_DEFAULT_REDIRECT;
    const {
        register,
        handleSubmit,
        watch,
        formState: { errors, isSubmitting },
    } = useForm<FormData>({
        resolver: zodResolver(joinOrganizationSchema),
        mode: 'onBlur',
        reValidateMode: 'onChange',
    });
    const onSubmit = async (data: FormData) => {
        if (!code) return;
        const payload: JoinOrganizationRequest = { code, password: data.password };
        setStatus('idle');
        setIsJoining(true);
        try {
            const res = await ServiceRegister.joinOrganization(payload);
            setStatus('success');
            setMessage('Account joined! Redirecting to the console…');
            // iam's one-time Keycloak link signs them in everywhere; else its tokens are handed over; else the console asks.
            const link = keycloakLink(res?.signInUrl);
            if (link) { await followSignInLink(link, undefined); return; }
            const { accessToken, refreshToken, idToken } = res?.tokens ?? {};
            const signedIn = !!(accessToken && refreshToken && idToken)
                && await auth.initWithExternalTokens({ token: accessToken, refreshToken, idToken }).catch(() => false);
            if (!signedIn) { window.location.href = defaultRedirect; return; }
            // This flow made the session: it's this tab's own, so it survives ending someone else's on the way.
            ownSession();
            await leaveWithSession({ target: defaultRedirect, endSignup: true });
        } catch (e) {
            const incoming_message = errorText(e, 'invite');
            setStatus('error');
            setMessage(incoming_message);
            // On failure, go back to password prompt
            setIsJoining(false);
        }
    };

    /**
     * "Accept invitation" — ask the server which path this address is on.
     *
     * Attempts the join with no password. The server is the only party that
     * knows whether the address has an account, and it answers with a status
     * rather than a disclosure: 401 means sign in, 400 means set a password.
     */
    const onAccept = async () => {
        if (!code) {
            setStatus('error');
            setMessage('This invite link is missing its code.');
            return;
        }
        setStatus('idle');
        setMessage('');
        setIsChecking(true);
        try {
            // No password: for a brand-new invitee this is REFUSED (400), which
            // is what reveals the password step. It never creates an account.
            await ServiceRegister.joinOrganization({ code, password: '' });
            // Succeeding without a password is not a flow we rely on, but if the
            // server ever allows it the user is in — treat it as done.
            setStatus('success');
            setMessage('Account joined! Redirecting to the console…');
            window.location.href = defaultRedirect;
        } catch (e) {
            const httpStatus = readApiError(e).status;
            if (httpStatus === 401) {
                // The address already has an account. Send them to sign in —
                // never show a create-a-password form for an account that
                // exists. Keycloak returns them here, where the page re-renders
                // as the invited user and offers Join.
                setStatus('info');
                setMessage('You already have a Dodil account. Taking you to sign in…');
                // Named for the invited address (iam's refusal says it): no chooser, no other session answering.
                signIn(window.location.href, signInAsFrom(e)).catch(() => {
                    setStatus('error');
                    setMessage('Dodil couldn\'t be reached. Check your connection and try again.');
                    setIsChecking(false);
                });
                return;
            }
            if (httpStatus === 403) {
                setStatus('error');
                setMessage(
                    'This invitation was issued to a different account. Sign out, then open it again from the account it was sent to.'
                );
                setIsChecking(false);
                return;
            }
            if (httpStatus === 400) {
                // No account yet: this is the genuinely-new invitee.
                setIsChecking(false);
                setHasAccepted(true);
                return;
            }
            setStatus('error');
            setMessage(
                errorText(e, 'invite', 'We could not open this invitation. Ask whoever invited you to send a new one.')
            );
            setIsChecking(false);
        }
    };

    return <form onSubmit={handleSubmit(onSubmit)}>
        {status !== 'idle' && (
            <Callout.Root
                mb="4"
                size="1"
                className="api-banner"
                variant="soft"
                color={status === 'success' ? 'green' : status === 'info' ? 'gray' : 'tomato'}
                role={status === 'error' ? 'alert' : undefined}
            >
                <Callout.Text>{message}</Callout.Text>
            </Callout.Root>
        )}
        {status !== 'success' &&
            <Flex direction="column" gap="3">

        {/* Step 1: one button. The server decides what comes next — a password
            form for a new account, or sign-in for one that already exists. */}
        {!hasAccepted && !isJoining && (
            <Flex direction="column" gap="3">
                <Text size="2">
                    You&apos;ve been invited to join{' '}
                    <strong>{organizationName || 'an organization'}</strong> on Dodil.
                </Text>
                <Button
                    radius={'full'}
                    type="button"
                    size="2"
                    variant="classic"
                    disabled={isChecking}
                    loading={isChecking}
                    onClick={() => void onAccept()}
                >
                    Accept invitation
                </Button>
            </Flex>
        )}

        {/* Step 2: only reached when the server said this address has NO account
            yet, so asking for a password is the right question. */}
        {hasAccepted && !isJoining && (
            <>
                <Text size="2" mb="1">
                    Choose a password to finish setting up your Dodil account.
                </Text>
                <NewPassword field={register('password')} value={watch('password') ?? ''} error={errors.password?.message} />

                <Button
                    radius={'full'}
                    type="submit"
                    size="2"
                    variant="classic"
                    disabled={isSubmitting}
                    loading={isSubmitting}
                >
                    Join
                </Button>

            </>
        )}

        {/* While joining: hide password fields and show progress text */}
        {isJoining && (
            <Callout.Root
                mt="4"
                size="2"
                variant="soft"
                color="gray"
                role="status"
            >
                <Callout.Text>Adding you to Dodil…</Callout.Text>
            </Callout.Root>
        )}
        </Flex>
        }
    </form>
}
